import { useState } from 'react';

import { confirmMfaCode, fallbackToSms } from 'api/LoginSecurity';

import { useSmartNavigate } from 'lib/hooks/useSmartNavigate';

/**
 * Encapsulates the MFA challenge step that sits between a successful password
 * auth (/api/user/login) and the post-login wiring. Parks the login response
 * until /api/user/mfa/confirm clears, so the FE never treats a partial-auth
 * session as fully authed.
 *
 * @param {Object} opts
 * @param {Object} opts.extraLoginParams - forwarded to /mfa/confirm (e.g.
 *   self-scheduling token) so the backend can finalize whatever the password
 *   step started.
 * @param {(data, res) => Promise<void>} opts.onComplete - called with the
 *   parked login response once MFA confirms.
 */
const useMfaChallenge = ({ extraLoginParams, onComplete }) => {
  const { navigate } = useSmartNavigate();
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);

  const start = ({ kind, mfaId, pendingData, pendingRes }) => {
    setChallenge({ kind: kind || null, mfaId: mfaId || null, pendingData, pendingRes, error: '' });
  };

  const reset = () => setChallenge(null);

  const submitCode = async (code) => {
    if (!challenge) return;
    setLoading(true);
    setChallenge((ps) => ({ ...ps, error: '' }));
    try {
      const res = await confirmMfaCode(navigate, { code, ...extraLoginParams });
      if (res?.code !== 0) {
        setChallenge((ps) => ({ ...ps, error: res?.error || 'Invalid code. Please try again.' }));
        return;
      }
      const { pendingData, pendingRes } = challenge;
      setChallenge(null);
      await onComplete(pendingData, pendingRes);
    } catch (err) {
      setChallenge((ps) => ({
        ...ps,
        error: err?.error || err?.message || 'Verification failed. Please try again.'
      }));
    } finally {
      setLoading(false);
    }
  };

  const switchToSms = async () => {
    if (!challenge) return;
    setLoading(true);
    setChallenge((ps) => ({ ...ps, error: '' }));
    try {
      const res = await fallbackToSms(navigate);
      if (res?.code !== 0) {
        setChallenge((ps) => ({
          ...ps,
          error: res?.error || 'Could not switch to SMS. Please try again.'
        }));
        return;
      }
      setChallenge((ps) => ({ ...ps, kind: 'sms', mfaId: res?.mfaId || ps.mfaId }));
    } catch (err) {
      setChallenge((ps) => ({
        ...ps,
        error: err?.error || err?.message || 'Could not switch to SMS. Please try again.'
      }));
    } finally {
      setLoading(false);
    }
  };

  return { challenge, loading, start, reset, submitCode, switchToSms };
};

export default useMfaChallenge;
